import type { ReactNode } from 'react';
import { Badge, Card, CardHeader, CardTitle } from '@paige/ui';
import { useFormat } from '@paige/format';
import type { EndpointHealth, IntakeSource } from '../types';
import type { AccountDevice } from './account-types';
import { intakeWord } from './BreakdownPanel';
import './insights.css';
import './account.css';

export interface IntakePanelProps {
  /** Pages in 30 days by where the batches arrived (AccountDetail.intake). */
  intake: readonly { source: string; pages: number; lastAt: string | null }[];
  /** The intake set up on the company's jobs: email addresses, folders, scanners. */
  configured: readonly EndpointHealth[];
  /** Connected computers and copiers: a watched folder or a scanner is intake set up too (round 3). */
  devices?: readonly AccountDevice[] | undefined;
}

/** Whether pages from a source have something set up: a job's address, a device's folder, or a device at all. */
function isSetUp(source: string, p: IntakePanelProps): boolean {
  if (source === 'upload' || source === 'demo') return true;
  if (p.configured.some((c) => c.kind === source)) return true;
  const devices = p.devices ?? [];
  if (source === 'folder') return devices.some((d) => d.folders > 0);
  return source === 'scanner' && devices.length > 0;
}

/**
 * Where pages come from (round 2, sales N2, support R2-M3): what actually arrived in 30 days, from the
 * batches, which is the truth (an attended Paige Scan or a copier panel sends with no source set up),
 * and then what is set up. The two disagreeing is itself worth a look, so it is said.
 */
export function IntakePanel(props: IntakePanelProps): ReactNode {
  const { intake, configured } = props;
  const unset = intake.filter((i) => !isSetUp(i.source, props));
  return (
    <Card id="ad-intake">
      <CardHeader>
        <CardTitle>Intake</CardTitle>
      </CardHeader>
      <h4 className="ad-small">Where pages arrived, last 30 days</h4>
      <Arrived intake={intake} />
      <h4 className="ad-small">Set up</h4>
      <SetUp configured={configured} devices={props.devices ?? []} />
      {unset.length ? (
        <p className="ad-small">
          {unset.map((u) => word(u.source)).join(' and ')} pages arrive without a source set up on a
          job: a Paige Scan or copier panel sends them directly.
        </p>
      ) : null}
    </Card>
  );
}

const word = (s: string) => intakeWord[s as IntakeSource] ?? s;

function Arrived({ intake }: { intake: IntakePanelProps['intake'] }): ReactNode {
  const f = useFormat();
  const total = intake.reduce((s, i) => s + i.pages, 0);
  if (total === 0) return <p className="ad-small">No pages in 30 days.</p>;
  return (
    <ul className="ad-intake">
      {intake.map((i) => (
        <li key={i.source}>
          <span>{word(i.source)}</span>
          <span className="ad-intake__track">
            <span
              className="ad-intake__bar"
              style={{ inlineSize: `${((i.pages / total) * 100).toFixed(1)}%` }}
            />
          </span>
          <span className="ad-small">
            {f.percent((i.pages / total) * 100, 0)} · {f.int(i.pages)} pages
            {i.lastAt ? ` · last ${f.ago(i.lastAt)}` : ''}
          </span>
        </li>
      ))}
    </ul>
  );
}

function SetUp(x: {
  configured: IntakePanelProps['configured'];
  devices: readonly AccountDevice[];
}): ReactNode {
  const { configured } = x;
  const watching = x.devices.filter((d) => d.folders > 0);
  if (configured.length === 0 && watching.length === 0)
    return <p className="ad-small">No email address, watched folder or scanner set up.</p>;
  return (
    <ul className="ad-intake__set">
      {watching.map((d) => (
        <li key={d.id}>
          {d.name} <Badge tone="neutral">watched folders</Badge>{' '}
          <span className="ad-small">{d.folders} on this computer</span>
        </li>
      ))}
      {configured.map((e) => (
        <li key={e.id}>
          {e.label ? `${e.label} ` : null}
          <Badge tone="neutral">{e.kind}</Badge> <span className="ad-small">{e.health}</span>
        </li>
      ))}
    </ul>
  );
}
